import { motion } from "framer-motion";
import {
  Landmark, Signal, ShoppingCart, Dices, Package, Car, HeartPulse, Pill,
  Shield, Building2, Plane, UtensilsCrossed, Pizza, GraduationCap, School,
  Trophy, Clapperboard, CalendarDays, Store, LandmarkIcon, HandHeart,
  Newspaper, Megaphone, Truck, Luggage, Cpu, Briefcase,
} from "lucide-react";

const INDUSTRIES = [
  { icon: Landmark, name: "Banking & Financial Services", text: "Promote branches, ATMs and financial products around commercial districts." },
  { icon: Signal, name: "Telecommunications", text: "Drive store visits and plan upgrades around high-traffic commuter zones." },
  { icon: ShoppingCart, name: "Retail", text: "Pull foot traffic into stores with precision offers around your outlets." },
  { icon: Dices, name: "Betting & Gaming", text: "Engage fans around stadiums, transport hubs and match-day venues." },
  { icon: Package, name: "FMCG", text: "Launch products around retail precincts and point-of-sale locations." },
  { icon: Car, name: "Automotive", text: "Promote dealerships and test-drive events around competitor zones." },
  { icon: HeartPulse, name: "Healthcare", text: "Reach communities around clinics, hospitals and wellness events." },
  { icon: Pill, name: "Pharmaceuticals", text: "Target healthcare professionals around conferences and facilities." },
  { icon: Shield, name: "Insurance", text: "Contextual offers around dealerships, campuses and business parks." },
  { icon: Building2, name: "Real Estate", text: "Promote show houses and developments around target suburbs." },
  { icon: Plane, name: "Tourism & Hospitality", text: "Welcome travellers around airports, hotels and attractions." },
  { icon: UtensilsCrossed, name: "Restaurants", text: "Drive lunch and dinner trade around office nodes and malls." },
  { icon: Pizza, name: "Fast Food", text: "Win the lunchtime battle around transport hubs and campuses." },
  { icon: GraduationCap, name: "Education", text: "Enrolment campaigns around schools and community centres." },
  { icon: School, name: "Universities", text: "Open-day and registration campaigns around campuses." },
  { icon: Trophy, name: "Sports", text: "Match-day activations around stadiums and fan parks." },
  { icon: Clapperboard, name: "Entertainment", text: "Premieres and launches around cinemas and nightlife districts." },
  { icon: CalendarDays, name: "Events", text: "One-day activations around expos, concerts and festivals." },
  { icon: Store, name: "Shopping Malls", text: "Tenant promotions and footfall campaigns around the mall precinct." },
  { icon: LandmarkIcon, name: "Government", text: "Citizen communication around service centres and civic venues." },
  { icon: HandHeart, name: "Public Services", text: "Awareness campaigns around clinics, libraries and community halls." },
  { icon: Newspaper, name: "Media", text: "Audience growth campaigns around commuter corridors." },
  { icon: Megaphone, name: "Advertising Agencies", text: "White-label geofencing for your client portfolio." },
  { icon: Truck, name: "Logistics", text: "Recruitment and service campaigns around depots and corridors." },
  { icon: Luggage, name: "Travel", text: "Offers around airports, stations and booking hotspots." },
  { icon: Cpu, name: "Technology", text: "Product launches around business parks and innovation hubs." },
  { icon: Briefcase, name: "SMMEs", text: "Affordable, measurable local advertising for growing businesses." },
];

export default function Industries() {
  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(29,78,216,0.14),transparent_50%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 relative">
          <p className="section-label">Industries</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-white tracking-tight max-w-3xl">
            BUILT FOR EVERY INDUSTRY THAT <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">MOVES IN THE PHYSICAL WORLD</span>
          </h1>
          <p className="mt-5 text-lg text-slate-400 max-w-2xl">
            From banks to betting brands, retailers to government — if your customers gather somewhere, GEOTRAQ AI can reach them there.
          </p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {INDUSTRIES.map((ind, i) => (
            <motion.div key={ind.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }}
              className="card-surface p-5 hover:border-cyan-500/30 transition-colors" data-testid={`industry-${i}`}>
              <ind.icon className="w-5 h-5 text-cyan-400" />
              <h3 className="mt-3 font-display font-semibold text-white text-sm">{ind.name}</h3>
              <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">{ind.text}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
